import useSWR from 'swr';

import { getBoxConditionCopy } from 'productTemplate/utils/getBoxConditionCopy';
import { getSpecialTypeDataMap } from 'productTemplate/utils/specialTypeDataMap';
import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { InstantShipIcon } from 'shared/icons/InstantShipIcon';
import { requestService } from 'shared/utils/services/requestService';
import { t } from 'localization/utils/t';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { VARIANT_KEYS } from 'productTemplate/enums/variantKeys';
import { VariantPriceListItem } from 'productTemplate/components/buyBar/VariantPriceListItem';

const TP = 'productTemplate.components.buyBar.VariantPriceListRows';

const fetchData = (url) => requestService({ url, method: 'GET' });

interface IVariantPriceListRowsProps {
  bestPriceVariant?: IBuyBarProductVariant;
  instantShipVariant?: IBuyBarProductVariant;
  newWithBoxIssuesVariant?: IBuyBarProductVariant;
  showNewWithBoxIssues?: boolean;
  newWithDefects?: IBuyBarProductVariant;
}

export const VariantPriceListRows = ({
  bestPriceVariant,
  instantShipVariant,
  newWithBoxIssuesVariant,
  showNewWithBoxIssues = false,
  newWithDefects,
}: IVariantPriceListRowsProps) => {
  const {
    id,
    localizedSpecialDisplayPriceCents,
    specialType,
    productType,
    isFashionProduct,
    directShipping,
  } = useStoreSelector((state) => state.pdp.productTemplate);
  const specialTypeDataMap = getSpecialTypeDataMap();
  const isSneaker = productType === 'sneakers';
  const { data: descriptionCopy } = useSWR(
    `/web-api/v1/product_variants/buy_bar_copies?productTemplateId=${id}`,
    fetchData,
  );

  const instantShipPriceIsLowestPrice =
    bestPriceVariant?.lowestPriceCents?.amount ===
    instantShipVariant?.instantShipLowestPriceCents?.amount;
  const isUnderRetail =
    bestPriceVariant?.lowestPriceCents?.amount < localizedSpecialDisplayPriceCents?.amount;

  const isSpecialTypeVariant = bestPriceVariant && specialType !== 'standard'; // Custom | ID | Vintage | Sample | etc
  const isBestPriceNonInstantVariant = bestPriceVariant && !instantShipPriceIsLowestPrice;
  const isNewWithBoxIssuesVariant = showNewWithBoxIssues && newWithBoxIssuesVariant;

  const getCondition = () => {
    if (isUnderRetail) {
      return isSneaker ? t(`${TP}.underRetail`, 'Under Retail') : t(`${TP}.sale`, 'Sale');
    }
    return t(`${TP}.bestPrice`, 'Best Price');
  };

  // If is non-standard (a.k.a special) product, it should only show one line item as "New / Custom" with View button to show grid of products
  if (isSpecialTypeVariant) {
    return (
      <VariantPriceListItem
        condition={specialTypeDataMap[specialType].title}
        description={specialTypeDataMap[specialType].description}
        isUnderRetail={isUnderRetail}
        withGridView="special"
        variant={bestPriceVariant}
      />
    );
  }

  return (
    <tbody>
      {isBestPriceNonInstantVariant && (
        <VariantPriceListItem
          condition={getCondition()}
          description={!isFashionProduct || !directShipping ? descriptionCopy?.web?.best : ''}
          isBestPrice
          isUnderRetail={isUnderRetail}
          variant={bestPriceVariant}
        />
      )}
      {instantShipVariant && (
        <VariantPriceListItem
          condition={<InstantShipIcon fill="#000000" width={51} height={14} />}
          description={descriptionCopy?.web?.instant}
          isInstant
          isUnderRetail={isUnderRetail}
          variant={instantShipVariant}
        />
      )}
      {isNewWithBoxIssuesVariant && (
        <VariantPriceListItem
          condition={getBoxConditionCopy(newWithBoxIssuesVariant?.boxCondition)}
          description={descriptionCopy?.web?.best}
          isUnderRetail={isUnderRetail}
          variant={newWithBoxIssuesVariant}
        />
      )}
      {newWithDefects && (
        <VariantPriceListItem
          condition={t(`${TP}.newDefects`, 'New with Defects')}
          description={t(
            `${TP}.newDefectsDescription`,
            'Items are new and unworn, but have imperfections that require you to review photos before purchasing.',
          )}
          isUnderRetail={isUnderRetail}
          variant={newWithDefects}
          withGridView={VARIANT_KEYS.NEW_WITH_DEFECTS}
        />
      )}
    </tbody>
  );
};
